/**
 * @file CatalogCardTab.tsx
 * @description Вкладка «Карточка товара» панели «Каталог» (phase19.16 Р2а;
 * uiux-design-r2a §2; db-design-r2a, ответ 5; P3; SB65): одна карточка на
 * весь сайт — каталог, «Похожие товары» и элемент «Товары».
 *
 * SB122 (uiux-settings-groups §5, шаг 5; §8 — вид карточек): шесть
 * групп-карточек в `SettingsGroups panel="catalog-card"` — Шаблон · Что на
 * карточке · Кнопка и фото · Вид карточки (SB31: как у всех / своё) ·
 * Подписи бейджей (свёрнута по умолчанию) · Шрифт (приёмка Р2а §7.17 п. 3:
 * все надписи — основным текстом). На ПК сверху оглавление-пилюли, свёрнутые
 * помнятся (`commora.settings-groups.catalog-card`); `?group=<id>` открывает
 * группу. Содержимое групп — `CatalogCardGroups`, `CardLookGroup`,
 * `BadgeLabelsGroup`.
 */

import { DEFAULT_SITE_THEME, type SiteCatalogOptions, type SiteColors, type SiteTheme } from "@chaiberi/shared";

import { SettingsGroups } from "../../ui/settings-groups";
import { BadgeLabelsGroup } from "./BadgeLabelsGroup";
import { CardLookGroup } from "./CardLookGroup";
import { CardButtonPhotoGroup, CardFieldsGroup, CardFontGroup, CardTemplateGroup } from "./CatalogCardGroups";
import { applyCardTemplate, type CardTemplateMemory } from "../../../lib/site-view/catalog-form";

export { OLD_PRICE_HINT } from "./CatalogCardGroups";

type Card = SiteCatalogOptions["card"];

interface Props {
  options: SiteCatalogOptions;
  onChange: (options: SiteCatalogOptions) => void;
  colors: SiteColors | null;
  /** Скругление и стиль карточек темы черновика; `null` — не загружено (умолчания). */
  shapes?: SiteTheme["shapes"] | null;
  /** Что шаблон поменял сам — вернуть при смене шаблона обратно (хранит панель). */
  memory: CardTemplateMemory;
  onMemory: (memory: CardTemplateMemory) => void;
}

/** Редкая группа — свёрнута по умолчанию на ПК. */
const DEFAULT_COLLAPSED: readonly string[] = ["badges"];

export function CatalogCardTab({ options, onChange, colors, shapes = null, memory, onMemory }: Props) {
  const card = options.card;
  const setCard = (patch: Partial<Card>) => onChange({ ...options, card: { ...card, ...patch } });
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-caption text-on-surface-variant">Эта карточка — в каталоге, «Похожих товарах» и элементе «Товары» на всех страницах.</p>
      <SettingsGroups panel="catalog-card" defaultCollapsed={DEFAULT_COLLAPSED}>
        <CardTemplateGroup
          card={card}
          colors={colors}
          memory={memory}
          onTemplate={(template) => {
            const next = applyCardTemplate(options, template, memory);
            onMemory(next.memory);
            onChange(next.options);
          }}
        />
        <CardFieldsGroup card={card} setCard={setCard} />
        <CardButtonPhotoGroup card={card} setCard={setCard} />
        <CardLookGroup look={card.look} onChange={(look) => setCard({ look })} themeShapes={shapes ?? DEFAULT_SITE_THEME.shapes} colors={colors} />
        <BadgeLabelsGroup options={options} onChange={onChange} />
        <CardFontGroup />
      </SettingsGroups>
    </div>
  );
}
